import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCredit } from '@/hooks/useCredit';
import AppGate from '@/components/feature/AppGate';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney,
  buildCustomerQrData,
  parseCustomerQrData,
  type CreditCustomer,
  type CreditTxType,
} from '@/lib/credit';
import { parseAccountQrData, buildAccountQrData } from '@/lib/accountQr';
import CarnetView from './components/CarnetView';
import ClientsView from './components/ClientsView';
import ReportsView from './components/ReportsView';
import SettingsView from './components/SettingsView';
import TransactionModal from './components/TransactionModal';
import CustomerFormModal from './components/CustomerFormModal';
import CredentialsModal from './components/CredentialsModal';
import ImportDebtsModal from './components/ImportDebtsModal';
import QrModal from './components/QrModal';
import PayChoiceModal from './components/PayChoiceModal';
import SettleDebtModal from './components/SettleDebtModal';
import QrScannerModal from '@/components/feature/QrScannerModal';

type Tab = 'clients' | 'rapports' | 'reglages';
type PickerPurpose = 'credit' | 'payment' | 'deposit' | 'statement';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'clients', label: 'Clients', icon: 'ri-group-line' },
  { key: 'rapports', label: 'Rapports', icon: 'ri-bar-chart-box-line' },
  { key: 'reglages', label: 'Réglages', icon: 'ri-settings-3-line' },
];

function CreditPageInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const credit = useCredit();
  const { currency, customers, transactions, loading, error, stats, reload } = credit;

  const [tab, setTab] = useState<Tab>('clients');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [search, setSearch] = useState('');
  const [txModal, setTxModal] = useState<{ mode: CreditTxType; customer: CreditCustomer } | null>(null);
  const [payChoice, setPayChoice] = useState<CreditCustomer | null>(null);
  const [settleTarget, setSettleTarget] = useState<CreditCustomer | null>(null);
  const [customerModal, setCustomerModal] = useState<{ customer?: CreditCustomer | null } | null>(null);
  const [picker, setPicker] = useState<PickerPurpose | null>(null);
  const [deleting, setDeleting] = useState<CreditCustomer | null>(null);
  const [qr, setQr] = useState<{ title: string; subtitle?: string; data: string } | null>(null);
  const [scanner, setScanner] = useState(false);
  const [scanError, setScanError] = useState('');
  const [creds, setCreds] = useState<{ customer: CreditCustomer; username: string | null; password: string | null } | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const lastActivity = useMemo(() => {
    const map: Record<number, string | null> = {};
    transactions.forEach((tx) => {
      if (!map[tx.customer_id] || new Date(tx.created_at) > new Date(map[tx.customer_id] as string)) {
        map[tx.customer_id] = tx.created_at;
      }
    });
    return map;
  }, [transactions]);

  const debtFor = (c: CreditCustomer) => Math.max(0, credit.balanceFor(c.id));

  const handleTransaction = async (amount: number, description: string, paymentMethod?: string, proofUrl?: string | null) => {
    if (!txModal) return;
    const { mode, customer } = txModal;
    if (mode === 'credit') await credit.addCredit(customer.id, amount, description, paymentMethod, proofUrl);
    else if (mode === 'payment') await credit.recordPayment(customer.id, amount, description, paymentMethod);
    else if (mode === 'deposit') await credit.addDeposit(customer.id, amount, description, paymentMethod);
    else await credit.addAdjustment(customer.id, amount, description);
  };

  // « Il paie » → choix Paiement de dette / Dépôt de fonds.
  const onPickPayMode = (mode: 'payment' | 'deposit') => {
    if (!payChoice) return;
    setTxModal({ mode, customer: payChoice });
    setPayChoice(null);
  };

  // « Tout est payé » → solde la dette restante avec le moyen de paiement choisi.
  const handleSettleDebt = async (paymentMethod: string) => {
    if (!settleTarget) return;
    await credit.settleDebt(settleTarget.id, paymentMethod);
  };

  const handleInvite = async (c: CreditCustomer) => {
    const token = await credit.inviteCustomer(c.id);
    const link = `${window.location.origin}/credit/join?token=${token}`;
    setQr({ title: `Inviter ${c.full_name}`, subtitle: 'Lien de rattachement client', data: link });
  };

  const handleScan = (raw: string) => {
    setScanError('');
    const text = raw.trim();
    const account = parseAccountQrData(text);
    const id = account?.type === 'customer' ? account.id : parseCustomerQrData(text);
    if (!id) {
      setScanError('Code QR non reconnu.');
      return;
    }
    if (!customers.some((c) => c.id === id)) {
      setScanError('Ce client n\u2019appartient pas à votre carnet.');
      return;
    }
    setScanner(false);
    navigate(`/dashboard/credit/customer/${id}`);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await credit.deleteCustomer(deleting.id);
    setDeleting(null);
  };

  const onPickerSelect = (c: CreditCustomer) => {
    const purpose = picker;
    setPicker(null);
    if (purpose === 'statement') navigate(`/dashboard/credit/customer/${c.id}?statement=1`);
    else if (purpose === 'deposit') setTxModal({ mode: 'deposit', customer: c });
    else if (purpose) setTxModal({ mode: purpose === 'credit' ? 'credit' : 'payment', customer: c });
  };

  return (
    <div className="p-4 md:p-6">
      {/* ===== Header ===== */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-book-2-line text-primary-500"></i>{t('crf.title_simple', { defaultValue: 'Carnet de crédit' })}
          </h2>
          <p className="text-sm text-foreground-500 mt-1">{user?.nomcommerce || 'Mon commerce'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setPicker('credit')}
            className="flex items-center gap-2 px-5 h-12 bg-primary-500 text-background-50 rounded-xl text-base font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>{t('crf.is_taking_credit', { defaultValue: 'Quelqu\u2019un prend à crédit' })}
          </button>
          <button
            onClick={() => setCustomerModal({})}
            className="flex items-center gap-2 px-5 h-12 bg-background-100 border border-background-200/70 text-foreground-800 rounded-xl text-base font-bold whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-user-add-line text-lg"></i>{t('crf.new_client', { defaultValue: 'Nouveau client' })}
          </button>
          <button
            onClick={() => { setScanError(''); setScanner(true); }}
            className="flex items-center gap-2 px-5 h-12 bg-foreground-950 text-background-50 rounded-xl text-base font-bold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-qr-scan-2-line text-lg"></i>{t('crf.scan_qr_big', { defaultValue: 'Scanner QR code' })}
          </button>
          <button
            onClick={() => setQr({
              title: 'Mon QR code marchand',
              subtitle: 'À faire scanner par vos clients',
              data: buildAccountQrData('merchant', user?.id || 0),
            })}
            className="flex items-center gap-2 px-5 h-12 bg-accent-500 text-background-50 rounded-xl text-base font-bold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-qr-code-line text-lg"></i>{t('crf.my_qr', { defaultValue: 'Mon QR code' })}
          </button>
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className={`flex items-center gap-2 px-4 h-12 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer border ${
              showAdvanced ? 'bg-foreground-950 text-background-50 border-foreground-950' : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className={showAdvanced ? 'ri-arrow-up-line' : 'ri-equalizer-line'}></i>{t('crf.more_options', { defaultValue: 'Plus d\u2019options' })}
          </button>
        </div>
      </div>

      {/* ===== Grands totaux ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-5">
        <div className="bg-primary-500 text-background-50 rounded-2xl p-5">
          <div className="text-sm font-medium text-background-50/80">{t('crf.total_owed_to_you', { defaultValue: 'On te doit' })}</div>
          <div className="text-3xl md:text-4xl font-bold font-heading mt-1">{formatMoney(stats.totalOutstanding, currency)}</div>
        </div>
        <div className="bg-secondary-500 text-background-50 rounded-2xl p-5">
          <div className="text-sm font-medium text-background-50/80">{t('crf.total_held', { defaultValue: 'Argent que tu gardes' })}</div>
          <div className="text-3xl md:text-4xl font-bold font-heading mt-1">{formatMoney(stats.totalDeposits, currency)}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
          <div className="text-sm font-medium text-foreground-500">{t('crf.clients_owe', { defaultValue: 'Clients qui doivent' })}</div>
          <div className="text-3xl md:text-4xl font-bold font-heading mt-1 text-foreground-950">{stats.customersWithCredit}</div>
        </div>
      </div>

      {/* ===== Grande recherche ===== */}
      <div className="relative mb-5">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-xl pointer-events-none"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('crf.search_client_big', { defaultValue: 'Chercher un client…' })}
          className="w-full h-14 pl-12 pr-4 rounded-2xl text-base bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
        />
      </div>

      {/* ===== Contenu ===== */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-foreground-600 mb-3 text-sm">{error}</p>
          <button onClick={reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
            {t('crf.retry', { defaultValue: 'Réessayer' })}
          </button>
        </div>
      ) : !showAdvanced ? (
        /* --- Vue simple : le carnet en grand format --- */
        <CarnetView
          customers={customers}
          balances={credit.balances}
          transactions={transactions}
          currency={currency}
          search={search}
          onNewCredit={(c) => setTxModal({ mode: 'credit', customer: c })}
          onPay={(c) => setPayChoice(c)}
          onSettleDebt={(c) => setSettleTarget(c)}
          onQr={(c) => setQr({ title: c.full_name, subtitle: 'QR client', data: buildCustomerQrData(c.id) })}
          onAddCustomer={() => setCustomerModal({})}
        />
      ) : (
        /* --- Vue avancée : clients détaillés, rapports, réglages + actions --- */
        <div className="space-y-4">
          {/* Actions avancées */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setImportOpen(true)}
              className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-700 rounded-xl text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
              <i className="ri-camera-lens-line"></i>{t('crf.photo_debts', { defaultValue: 'Photo dettes' })}
            </button>
            <button onClick={() => setScanner(true)}
              className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-700 rounded-xl text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
              <i className="ri-scan-line"></i>{t('crf.scan_qr', { defaultValue: 'Scanner un QR' })}
            </button>
            <button onClick={() => setPicker('statement')}
              className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-700 rounded-xl text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
              <i className="ri-file-text-line"></i>{t('crf.statement', { defaultValue: 'Relevé' })}
            </button>
            <button onClick={() => setPicker('payment')}
              className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-700 rounded-xl text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
              <i className="ri-hand-coin-line"></i>{t('crf.record_payment', { defaultValue: 'Enregistrer un paiement' })}
            </button>
          </div>

          {/* Onglets avancés */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto max-w-full">
            {TABS.map((tabItem) => (
              <button key={tabItem.key} onClick={() => setTab(tabItem.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
                  tab === tabItem.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}>
                <i className={`${tabItem.icon} text-sm`}></i>{t(`credit.tab.${tabItem.key}`, { defaultValue: tabItem.label })}
              </button>
            ))}
          </div>

          {tab === 'clients' ? (
            <ClientsView
              customers={customers.filter((c) => !search.trim() || c.full_name.toLowerCase().includes(search.trim().toLowerCase()))}
              balances={credit.balances} currency={currency} lastActivity={lastActivity}
              onNewCredit={(c) => setTxModal({ mode: 'credit', customer: c })}
              onPayment={(c) => setTxModal({ mode: 'payment', customer: c })}
              onEdit={(c) => setCustomerModal({ customer: c })}
              onDelete={(c) => setDeleting(c)}
              onInvite={handleInvite}
              onCredentials={(c) => setCreds({ customer: c, username: c.username, password: c.temp_password })}
              onQr={(c) => setQr({ title: c.full_name, subtitle: 'QR client', data: buildCustomerQrData(c.id) })}
            />
          ) : tab === 'rapports' ? (
            <ReportsView customers={customers} transactions={transactions} currency={currency} />
          ) : (
            <SettingsView
              settings={credit.settings} currency={currency} reminders={credit.reminders}
              merchantId={user?.id || 0} onSave={credit.saveSettings}
            />
          )}
        </div>
      )}

      {/* ==== Modals ==== */}
      {txModal && (
        <TransactionModal
          mode={txModal.mode} customer={txModal.customer} currency={currency}
          balance={credit.balanceFor(txModal.customer.id)}
          onClose={() => setTxModal(null)}
          onSubmit={handleTransaction}
        />
      )}

      {payChoice && (
        <PayChoiceModal
          customer={payChoice}
          currency={currency}
          balance={credit.balanceFor(payChoice.id)}
          onChoose={onPickPayMode}
          onClose={() => setPayChoice(null)}
        />
      )}

      {settleTarget && (
        <SettleDebtModal
          customer={settleTarget}
          debt={debtFor(settleTarget)}
          currency={currency}
          onConfirm={handleSettleDebt}
          onClose={() => setSettleTarget(null)}
        />
      )}

      {customerModal && (
        <CustomerFormModal
          customer={customerModal.customer}
          currency={currency}
          onClose={() => setCustomerModal(null)}
          onSubmit={async (data) => {
            if (customerModal.customer) {
              await credit.updateCustomer(customerModal.customer.id, data);
            } else {
              const created = await credit.createCustomer(data);
              setCreds({ customer: created, username: created.username, password: created.temp_password });
            }
          }}
        />
      )}

      {picker && (
        <CustomerPickerModal
          purpose={picker}
          customers={customers}
          onClose={() => setPicker(null)}
          onSelect={onPickerSelect}
        />
      )}

      {qr && <QrModal title={qr.title} subtitle={qr.subtitle} data={qr.data} onClose={() => setQr(null)} />}

      {creds && (
        <CredentialsModal
          customerName={creds.customer.full_name || 'Client'}
          username={creds.username}
          password={creds.password}
          onRegenerate={async () => {
            const next = await credit.regenerateCredentials(creds.customer.id);
            setCreds({ customer: creds.customer, username: next.username, password: next.password });
            return next;
          }}
          onClose={() => setCreds(null)}
        />
      )}

      {importOpen && (
        <ImportDebtsModal
          customers={customers}
          currency={currency}
          importBatches={credit.importBatches}
          onCreateCustomer={credit.createCustomer}
          onAddCredit={(customerId, amount, description) => credit.addCredit(customerId, amount, description)}
          onCreateBatch={credit.createImportBatch}
          onRevertBatch={credit.revertImportBatch}
          onClose={() => setImportOpen(false)}
          onDone={(createdList) => {
            if (createdList.length > 0) {
              const first = createdList[0];
              setCreds({ customer: first, username: first.username, password: first.temp_password });
            }
          }}
        />
      )}

      {deleting && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setDeleting(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer le client ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Son historique de crédit sera aussi supprimé.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer flex items-center gap-2">
                <i className="ri-delete-bin-line"></i>Supprimer
              </button>
            </div>
          </div>
        </>
      )}

      {scanner && (
        <QrScannerModal
          title="Scanner un QR client"
          hint="Placez le code QR du client dans le cadre. Vous serez redirigé vers son profil de crédit."
          onResult={handleScan}
          onClose={() => { setScanner(false); }}
        />
      )}

      {scanError && !scanner && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap">
          {scanError}
        </div>
      )}
    </div>
  );
}

export default function CreditPage() {
  return (
    <AppGate
      nompage="credit"
      appName="Zifek Crédit"
      icon="ri-book-2-line"
      appDescription="Le carnet de crédit numérique pour les commerces de proximité. Installez-le depuis l'AppStore pour commencer à gérer les crédits de vos clients."
    >
      <CreditPageInner />
    </AppGate>
  );
}

// ── Sélecteur de client (pour les actions rapides) ──────────────
function CustomerPickerModal({
  purpose, customers, onClose, onSelect,
}: {
  purpose: PickerPurpose;
  customers: CreditCustomer[];
  onClose: () => void;
  onSelect: (c: CreditCustomer) => void;
}) {
  const [q, setQ] = useState('');
  const titleMap: Record<PickerPurpose, string> = {
    credit: 'Ajouter un crédit — choisir le client',
    payment: 'Enregistrer un paiement — choisir le client',
    deposit: 'Déposer des fonds — choisir le client',
    statement: 'Générer un relevé — choisir le client',
  };
  const filtered = customers.filter((c) => !q.trim() || (c.full_name || '').toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
            <h3 className="text-sm font-bold text-foreground-950">{titleMap[purpose]}</h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>
          <div className="p-4 border-b border-background-200/70">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              <input type="text" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..."
                className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm bg-background-100 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="py-10 text-center text-sm text-foreground-400">Aucun client</p>
            ) : filtered.map((c) => (
              <button key={c.id} onClick={() => onSelect(c)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors cursor-pointer text-left">
                <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {(c.full_name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground-900 truncate">{c.full_name || 'Client'}</div>
                  <div className="text-xs text-foreground-400 truncate">{c.phone || 'Sans téléphone'}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}